import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ModuleAccessContext = createContext();

export const ModuleAccessProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [moduleAccess, setModuleAccess] = useState({
    accessible_modules: [],
    plan_name: 'Free Trial',
    plan_id: 'free_trial',
    business_type: 'retail_store',
    loading: true
  });

  // Check if current user is admin (admin role sees all modules)
  const isAdmin = user?.role === 'admin';

  const fetchModuleAccess = useCallback(async () => {
    if (!token) {
      setModuleAccess(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/subscriptions/my-access`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        const data = await res.json();
        setModuleAccess({
          accessible_modules: data.accessible_modules || [],
          locked_modules: data.locked_modules || [],
          plan_name: data.plan_name || 'Free Trial',
          plan_id: data.plan_id || 'free_trial',
          business_type: data.business_type || 'retail_store',
          module_details: data.module_details || [],
          loading: false
        });
      } else {
        setModuleAccess(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Failed to fetch module access:', err);
      setModuleAccess(prev => ({ ...prev, loading: false }));
    }
  }, [token]);

  useEffect(() => {
    fetchModuleAccess();
  }, [fetchModuleAccess]);

  // Check if user can access a specific module
  const canAccessModule = (moduleId) => {
    // Admin users can access ALL modules
    if (isAdmin) return true;
    
    // Core modules always accessible
    const coreModules = ['dashboard', 'reports', 'settings'];
    if (coreModules.includes(moduleId)) return true;
    
    return moduleAccess.accessible_modules.includes(moduleId);
  };

  // Check if module is locked (requires upgrade)
  const isModuleLocked = (moduleId) => {
    return moduleAccess.locked_modules?.includes(moduleId) || false;
  };

  // Get module info
  const getModuleInfo = (moduleId) => {
    return moduleAccess.module_details?.find(m => m.id === moduleId);
  };

  const value = {
    ...moduleAccess,
    canAccessModule,
    isModuleLocked,
    getModuleInfo,
    refreshAccess: fetchModuleAccess,
    isAdmin
  };

  return (
    <ModuleAccessContext.Provider value={value}>
      {children}
    </ModuleAccessContext.Provider>
  );
};

export const useModuleAccess = () => {
  const context = useContext(ModuleAccessContext);
  if (!context) {
    throw new Error('useModuleAccess must be used within ModuleAccessProvider');
  }
  return context;
};

export default ModuleAccessContext;
